import React from 'react'
import { observable, action, computed, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepContent from '@material-ui/core/StepContent'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Chip from '@material-ui/core/Chip'
import Tooltip from '@material-ui/core/Tooltip'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { useStore } from '../../../../stores/root.store'
import { extractYamlMentions, parseYamlPlan, ImportWarning, YamlMentions, FieldMention } from '../../../../utils/yaml-plan-parser'
import Organization from '../../../../models/settings/Organization.model'

const uuidv4 = require('uuid/v4')
const SKIP_VALUE = '__skip__'

// ─── Shared singleton state ───────────────────────────────────────────────────

class ImporterState {
    @observable yamlText: string = ''
    @observable mentions: YamlMentions | null = null
    @observable parseError: string | null = null
    @observable roleResolutions = new Map<string, string>()
    @observable fieldResolutions = new Map<string, string>()
    @observable activeStep: number = 0
    @observable isOpen: boolean = false

    @computed get unmappedRoles(): string[] { return this.mentions?.roleNames ?? [] }
    @computed get fieldMentions(): FieldMention[] { return this.mentions?.fieldMentions ?? [] }
    @computed get hasRoleStep(): boolean { return this.unmappedRoles.length > 0 }
    @computed get hasFieldStep(): boolean { return this.fieldMentions.length > 0 }

    @computed get steps(): string[] {
        const s = ['input']
        if (this.hasRoleStep) s.push('roles')
        if (this.hasFieldStep) s.push('fields')
        s.push('review')
        return s
    }

    @action reset = () => {
        this.yamlText = ''
        this.mentions = null
        this.parseError = null
        this.roleResolutions.clear()
        this.fieldResolutions.clear()
        this.activeStep = 0
        this.isOpen = false
    }

    @action open = () => { this.isOpen = true }

    @action parseAndAdvance = () => {
        const m = extractYamlMentions(this.yamlText)
        if (m.parseError) { this.parseError = m.parseError; return }
        this.parseError = null
        this.mentions = m
        this.activeStep = 1
    }

    @action setRoleResolution = (name: string, v: string) => { this.roleResolutions.set(name, v) }
    @action setFieldResolution = (name: string, v: string) => { this.fieldResolutions.set(name, v) }
    @action nextStep = () => { this.activeStep = Math.min(this.activeStep + 1, this.steps.length - 1) }
    @action prevStep = () => { this.activeStep = Math.max(this.activeStep - 1, 0) }

    buildResolutionMaps() {
        const roleMap = new Map<string, string>()
        const fieldMap = new Map<string, string>()
        this.roleResolutions.forEach((v, k) => { if (v !== SKIP_VALUE) roleMap.set(k, v) })
        this.fieldResolutions.forEach((v, k) => { if (v !== SKIP_VALUE) fieldMap.set(k, v) })
        return { roleMap, fieldMap }
    }
}

const state = new ImporterState()

// ─── Trigger button ───────────────────────────────────────────────────────────

export const YamlPlanImporterTrigger = observer(() => (
    <Tooltip title="Import a plan structure from an AI-generated YAML draft">
        <Button
            variant={state.isOpen ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={state.isOpen ? state.reset : state.open}
        >
            {state.isOpen ? 'Cancel Import' : 'Import YAML'}
        </Button>
    </Tooltip>
))

// ─── Panel ────────────────────────────────────────────────────────────────────

export const YamlPlanImporterPanel = observer(() => {
    const createStore     = useStore('createStore')
    const organizationStore = useStore('organizationStore')
    const organization    = organizationStore.organization

    if (!state.isOpen) return null

    const handleFileUpload = action((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = action((evt) => {
            state.yamlText = (evt.target?.result as string) || ''
            state.parseError = null
        })
        reader.readAsText(file)
        e.target.value = ''
    })

    const handleApply = async () => {
        const { roleMap, fieldMap } = state.buildResolutionMaps()

        // Auto-create fields that have add_new=true and no resolved match
        if (organization) {
            for (const mention of state.fieldMentions) {
                const orgMatch = organization.customersFields.find(
                    (f) => f.name.toLowerCase() === mention.name.toLowerCase() && !f.isDeleted
                )
                const alreadyResolved = fieldMap.has(mention.name)
                if (!orgMatch && !alreadyResolved && mention.addNew) {
                    try {
                        const newId = uuidv4()
                        await organizationStore.addCustomerField({
                            id: newId,
                            name: mention.name,
                            type: mention.fieldType,
                            isDeleted: false,
                            canBeDeleted: true
                        })
                        fieldMap.set(mention.name, newId)
                    } catch (err) {
                        console.error(`Failed to auto-create field "${mention.name}"`, err)
                    }
                }
            }
        }

        runInAction(() => {
            const result = parseYamlPlan(state.yamlText, organization, roleMap, fieldMap)
            createStore.importFromYaml(result)
            state.reset()
        })
    }

    return (
        <div style={{ border: '2px dashed #1976d2', borderRadius: 6, padding: '12px 16px', marginBottom: 16, background: '#f0f7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <Typography variant="subtitle2" style={{ color: '#1976d2' }}>
                    Import from AI YAML Draft
                </Typography>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Tooltip title="Copy the AI guide to your clipboard — paste it to ChatGPT / Claude to generate a valid YAML">
                        <Button size="small" variant="outlined" color="primary"
                            onClick={() => navigator.clipboard.writeText(AI_GUIDE_TEXT).catch(() => {})}
                        >
                            Copy AI Guide
                        </Button>
                    </Tooltip>
                    <Button size="small" onClick={state.reset} style={{ minWidth: 0 }}>✕</Button>
                </div>
            </div>

            {createStore.importedFromYaml && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 4, padding: '6px 10px', marginBottom: 10 }}>
                    <WarningIcon style={{ color: '#f57c00', fontSize: 18 }} />
                    <Typography variant="caption" style={{ color: '#e65100' }}>
                        This plan already has imported content. Applying will override current paths, roles and customer fields.
                    </Typography>
                </div>
            )}

            <Stepper activeStep={state.activeStep} orientation="vertical" style={{ background: 'transparent', padding: 0 }}>

                {/* Step 0 – Input */}
                <Step key="input">
                    <StepLabel>Paste or upload YAML</StepLabel>
                    <StepContent>
                        <label
                            htmlFor="yaml-file-upload"
                            style={{ display: 'inline-block', padding: '4px 10px', border: '1px solid #bbb', borderRadius: 4, cursor: 'pointer', fontSize: 13, background: '#fff', marginBottom: 8 }}
                        >
                            Upload .yaml / .yml
                        </label>
                        <input id="yaml-file-upload" type="file" accept=".yaml,.yml" style={{ display: 'none' }} onChange={handleFileUpload} />

                        <textarea
                            placeholder={YAML_PLACEHOLDER}
                            value={state.yamlText}
                            onChange={action((e) => { state.yamlText = e.target.value; state.parseError = null })}
                            style={{ width: '100%', minHeight: 160, fontFamily: 'monospace', fontSize: 12, padding: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4, resize: 'vertical', display: 'block' }}
                        />

                        {state.parseError && (
                            <Typography variant="caption" style={{ color: '#d32f2f', display: 'block', marginTop: 4 }}>
                                Parse error: {state.parseError}
                            </Typography>
                        )}
                        <div style={{ marginTop: 8 }}>
                            <Button variant="contained" color="primary" size="small" onClick={state.parseAndAdvance} disabled={!state.yamlText.trim()}>
                                Next
                            </Button>
                        </div>
                    </StepContent>
                </Step>

                {/* Step 1 (conditional) – Map Roles */}
                {state.hasRoleStep && (
                    <Step key="roles">
                        <StepLabel>Map roles to organization</StepLabel>
                        <StepContent>
                            <Typography variant="caption" style={{ display: 'block', marginBottom: 8 }}>
                                Select the matching organization role for each YAML role, or skip.
                            </Typography>
                            {state.unmappedRoles.map((name) => (
                                <RoleResolver
                                    key={name} roleName={name} organization={organization}
                                    value={state.roleResolutions.get(name) || ''}
                                    onChange={(v) => state.setRoleResolution(name, v)}
                                />
                            ))}
                            <StepNav onBack={state.prevStep} onNext={state.nextStep} />
                        </StepContent>
                    </Step>
                )}

                {/* Step 2 (conditional) – Map / create Customer Fields */}
                {state.hasFieldStep && (
                    <Step key="fields">
                        <StepLabel>Map customer fields to organization</StepLabel>
                        <StepContent>
                            <Typography variant="caption" style={{ display: 'block', marginBottom: 8 }}>
                                Pick a matching field, or leave blank — fields marked <strong>add_new</strong> will be created automatically.
                            </Typography>
                            {state.fieldMentions.map((mention) => (
                                <FieldResolver
                                    key={mention.name} mention={mention} organization={organization}
                                    value={state.fieldResolutions.get(mention.name) || ''}
                                    onChange={(v) => state.setFieldResolution(mention.name, v)}
                                />
                            ))}
                            <StepNav onBack={state.prevStep} onNext={state.nextStep} />
                        </StepContent>
                    </Step>
                )}

                {/* Last step – Review & Apply */}
                <Step key="review">
                    <StepLabel>Review & apply</StepLabel>
                    <StepContent>
                        <ReviewStep organization={organization} onBack={state.prevStep} onApply={handleApply} />
                    </StepContent>
                </Step>

            </Stepper>
        </div>
    )
})

// ─── Sub-components ───────────────────────────────────────────────────────────

const RoleResolver = observer(({ roleName, organization, value, onChange }: {
    roleName: string; organization: Organization | undefined; value: string; onChange: (v: string) => void
}) => {
    const orgRoles = organization?.roles || []
    const autoMatch = orgRoles.find((r) => r.text.toLowerCase() === roleName.toLowerCase())
    const effectiveValue = value || (autoMatch ? autoMatch.id : '')

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Chip label={roleName} size="small" style={{ minWidth: 110 }} />
            <Typography variant="caption" style={{ color: '#888' }}>→</Typography>
            <TextField
                select size="small" style={{ minWidth: 180 }}
                value={effectiveValue} onChange={(e) => onChange(e.target.value)}
                label="Org role" variant="outlined" InputLabelProps={{ shrink: true }}
            >
                <MenuItem value={SKIP_VALUE}><em>Skip (leave as-is)</em></MenuItem>
                {orgRoles.map((r) => <MenuItem key={r.id} value={r.id}>{r.text}</MenuItem>)}
            </TextField>
            {autoMatch
                ? <Typography variant="caption" style={{ color: '#388e3c' }}><CheckCircleIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /> auto-matched</Typography>
                : <Typography variant="caption" style={{ color: '#f57c00' }}><WarningIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /> not found</Typography>
            }
        </div>
    )
})

const FieldResolver = observer(({ mention, organization, value, onChange }: {
    mention: FieldMention; organization: Organization | undefined; value: string; onChange: (v: string) => void
}) => {
    const orgFields = (organization?.customersFields || []).filter((f) => !f.isDeleted)
    const autoMatch = orgFields.find((f) => f.name.toLowerCase() === mention.name.toLowerCase())
    const effectiveValue = value || (autoMatch ? autoMatch.id : '')

    // Determine status badge
    let badge: React.ReactNode
    if (autoMatch && !value) {
        badge = <Typography variant="caption" style={{ color: '#388e3c' }}><CheckCircleIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /> auto-matched</Typography>
    } else if (!autoMatch && !value && mention.addNew) {
        badge = <Typography variant="caption" style={{ color: '#1976d2' }}><AddCircleIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /> will be created ({mention.fieldType})</Typography>
    } else if (!autoMatch && !value) {
        badge = <Typography variant="caption" style={{ color: '#f57c00' }}><WarningIcon style={{ fontSize: 14, verticalAlign: 'middle' }} /> not found — will be skipped</Typography>
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Chip label={mention.name} size="small" style={{ minWidth: 110, maxWidth: 200 }} />
            <Typography variant="caption" style={{ color: '#888' }}>→</Typography>
            <TextField
                select size="small" style={{ minWidth: 180 }}
                value={effectiveValue} onChange={(e) => onChange(e.target.value)}
                label="Org field" variant="outlined" InputLabelProps={{ shrink: true }}
            >
                <MenuItem value={SKIP_VALUE}><em>Skip (exclude from plan)</em></MenuItem>
                {orgFields.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
            </TextField>
            {badge}
        </div>
    )
})

const ReviewStep = observer(({ organization, onBack, onApply }: {
    organization: Organization | undefined; onBack: () => void; onApply: () => void
}) => {
    const { roleMap, fieldMap } = state.buildResolutionMaps()
    const result = parseYamlPlan(state.yamlText, organization, roleMap, fieldMap)
    const eventCount = result.paths.reduce((acc, p) => acc + p.events.length, 0)
    const errors = result.warnings.filter((w) => w.type === 'parse_error' || w.type === 'broken_dependency')
    const infos  = result.warnings.filter((w) => w.type === 'unmapped_role' || w.type === 'unmapped_field')

    // Count fields that will be auto-created
    const toCreate = state.fieldMentions.filter((m) => {
        const orgMatch = organization?.customersFields.find((f) => f.name.toLowerCase() === m.name.toLowerCase() && !f.isDeleted)
        return !orgMatch && !fieldMap.has(m.name) && m.addNew
    })

    return (
        <div>
            <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 600 }}>
                Ready to import — will override current plan content:
            </Typography>
            <ul style={{ margin: '0 0 8px 0', paddingLeft: 18, fontSize: 13 }}>
                <li>{result.paths.length} path(s), {eventCount} event(s)</li>
                {result.roles.length > 0 && <li>{result.roles.length} plan role(s)</li>}
                {result.customerDetails.length > 0 && <li>{result.customerDetails.length} customer field(s) linked</li>}
                {toCreate.length > 0 && (
                    <li style={{ color: '#1976d2' }}>
                        {toCreate.length} new org field(s) will be created: {toCreate.map((f) => f.name).join(', ')}
                    </li>
                )}
            </ul>
            {errors.length > 0 && <WarningList title="Issues (applied as-is):" warnings={errors} color="#d32f2f" />}
            {infos.length > 0  && <WarningList title="Unresolved — fix after import:" warnings={infos} color="#f57c00" />}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button size="small" onClick={onBack}>Back</Button>
                <Button variant="contained" color="primary" size="small" onClick={onApply}>
                    Apply to Plan
                </Button>
            </div>
        </div>
    )
})

const StepNav = ({ onBack, onNext }: { onBack: () => void; onNext: () => void }) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button size="small" onClick={onBack}>Back</Button>
        <Button variant="contained" color="primary" size="small" onClick={onNext}>Next</Button>
    </div>
)

const WarningList = ({ title, warnings, color }: { title: string; warnings: ImportWarning[]; color: string }) => (
    <div style={{ marginBottom: 8 }}>
        <Typography variant="caption" style={{ fontWeight: 600, color }}>{title}</Typography>
        <ul style={{ margin: '2px 0 0 0', paddingLeft: 18 }}>
            {warnings.map((w, i) => <li key={i} style={{ color, fontSize: 12 }}>{w.message}</li>)}
        </ul>
    </div>
)

const AI_GUIDE_TEXT = `You are helping generate a plan structure in YAML format for the SimPlan back-office importer.

## Top-level fields
plan_title: "My Plan Name"
plan_type: Multi Users   # Options: Single | Multi Users | Multi | Template | Sales | Sharing
description: "Optional"

## Roles  (access: admin | editor | viewer)
roles:
  - name: Manager
    access: admin
  - name: Coordinator
    access: editor

## Customer Fields
customer_fields:
  - name: Project Name
    required: true
    add_new: false   # false=must exist in org; true=create if missing
    field_type: Text  # Text | Number | Text/Number | Phone | Email | List | Long Text | Date | File

## Paths & Events
paths:
  - title: Path Title
    show_on_preview: true
    parallel_path: None   # or title of another path to run in parallel
    events:
      - title: Event Title          # MUST be unique across all events
        message: "What to do here."
        event_type: With Actions    # With Actions | Checkboxes | Tip | Form
        show_on_preview: true
        executors: [Manager]        # role names
        viewers: [Coordinator]
        actions:
          - label: Done
            action_type: End        # End | Remind | Add Picture | Add File | Link
        dependencies:
          - mode: time
            time_offset: 0 Minutes From the Start
          - mode: event
            after_event: "Other Event Title"    # exact event title
            dependent_action: "Done"            # exact action label (or checkbox label)

## Dependency OR / AND logic:
# Each item in dependencies[] is an OR group — the event triggers if ANY group is satisfied.
# To require multiple events at once (AND), nest them as an inner array:
#
# OR example — triggers when either Step A OR Step B finishes:
#   dependencies:
#     - mode: event
#       after_event: Step A
#     - mode: event
#       after_event: Step B
#
# AND example — triggers only when BOTH Step A AND Step B finish:
#   dependencies:
#     - - mode: event
#         after_event: Step A
#       - mode: event
#         after_event: Step B
#
# Mixed OR/AND — triggers when (A AND B) OR C:
#   dependencies:
#     - - mode: event
#         after_event: Step A
#       - mode: event
#         after_event: Step B
#     - mode: event
#       after_event: Step C

## Event type notes:
# Checkboxes: actions[] = the checkbox items (labels only, no action_type needed)
# With Actions: actions[] = the button options
# Dependency dependent_action = checkbox label for Checkboxes events, action label for With Actions

## Rules:
# - Event titles must be unique (used as dependency references)
# - dependent_action must exactly match the action/checkbox label
# - parallel_path: set on the secondary path, value = primary path title
# - Roles & fields are matched by name to the organization — close matches are auto-resolved in the importer
`

const YAML_PLACEHOLDER = `# AI-generated YAML draft
plan_title: My Plan
plan_type: Multi Users

roles:
  - name: admin
    access: admin

customer_fields:
  - name: Phone
    required: true
    add_new: false
    field_type: Phone

paths:
  - title: Main Path
    events:
      - title: Step 1
        message: Check what applies.
        event_type: Checkboxes
        executors: [admin]
        actions:
          - label: Option A
          - label: Option B
        dependencies:
          - mode: time
            time_offset: 0 Minutes From the Start
      - title: Step 2
        event_type: With Actions
        executors: [admin]
        actions:
          - label: Done
            action_type: End
        dependencies:
          - mode: event
            after_event: Step 1
            dependent_action: Option A
`

export default YamlPlanImporterPanel
